import * as Popover from "@radix-ui/react-popover";
import {
  AnimatePresence,
  motion,
  Reorder,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import { useRef, useState } from "react";
import {
  TbArrowBarDown,
  TbArrowBarUp,
  TbDotsVertical,
  TbSeparatorHorizontal,
  TbTrash,
} from "react-icons/tb";
import { useMediaQuery } from "react-responsive";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import Button from "../../../components/Button";
import Checkbox from "../../../components/Checkbox";
import { Body, Bold, Caption2 } from "../../../styles/typography";
import ChapterDatePills, {
  formatDate,
  MOBILE_BREAKPOINT,
} from "./ChapterDatePills";

// Column tracks are identical in select and reorder mode (minus the
// checkbox column, which reorder mode drops entirely) so toggling the mode
// never shifts the remaining layout: only the last column swaps its content
// (updated date <-> drag handle).
export const DESKTOP_GRID = "44px 36px 1fr 140px 140px 44px";
export const DESKTOP_REORDER_GRID = "36px 1fr 140px 140px";
export const MOBILE_SELECT_GRID = "44px 1fr 44px";
export const MOBILE_ROW_GRID = "62px 1fr auto";
export { MOBILE_BREAKPOINT };

// The shared Checkbox positions its box absolutely 8px above the label;
// this override recenters it inside a grid cell.
export const CHECKBOX_STYLE = {
  margin: "8px 0 0",
  padding: 0,
  width: "24px",
  height: "24px",
};

const NARROW_BREAKPOINT = 480;

const getScrollParent = (node) => {
  let el = node instanceof Element ? node.parentElement : null;
  while (el && el !== document.body) {
    const { overflowY } = window.getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

const movedFlash = (props) => keyframes`
  from {
    background-color: rgba(${props.theme.colors.pineapple100}, 0.6);
  }
  to {
    background-color: rgb(${props.theme.colors.white});
  }
`;

const Row = styled(Reorder.Item)`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$reorderMode ? DESKTOP_REORDER_GRID : DESKTOP_GRID};
  align-items: center;
  gap: 12px;
  padding: 0.5rem 1rem;
  min-height: 64px;
  box-sizing: border-box;
  position: relative;
  background: rgb(${(props) => props.theme.colors.white});
  border: 2px solid
    rgb(
      ${(props) =>
        props.$dragging ? props.theme.colors.neutral400 : "transparent"}
    );
  border-bottom: 2px solid
    rgb(
      ${(props) =>
        props.$dragging
          ? props.theme.colors.neutral400
          : props.theme.colors.neutral150}
    );
  border-radius: ${(props) => (props.$dragging ? "8px" : "0")};
  box-shadow: ${(props) =>
    props.$dragging ? props.theme.shadows.shadow600() : "none"};
  overflow: hidden;
  ${(props) =>
    !props.$reorderMode &&
    css`
      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          background: rgb(${props.theme.colors.offWhite});
        }
      }
    `}
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${(props) =>
      props.$reorderMode
        ? css`
            grid-template-columns: ${MOBILE_ROW_GRID};
            align-items: stretch;
            gap: 0;
            padding: 0;
          `
        : css`
            grid-template-columns: ${MOBILE_SELECT_GRID};
          `}
    ${(props) =>
      props.$moved &&
      css`
        animation: ${movedFlash} 900ms ease;
      `}
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
`;

const PositionNumber = styled(Bold)`
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-size: 1rem;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const PositionBadge = styled(Bold)`
  display: none;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    margin-top: 1px;
    border-radius: 8px;
    color: rgb(${(props) => props.theme.colors.neutral600});
  }
`;

const PositionPlain = styled(Body)`
  display: none;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-weight: 400;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 26px;
    margin-top: 1px;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
  min-width: 0;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${(props) => (props.$reorderMode ? "14px 16px" : "0.5rem 0")};
  }
`;

const TitleLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const TitleCell = styled(Bold)`
  color: rgb(${(props) => props.theme.colors.neutral600});
  font-size: 1rem;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 17px;
    line-height: 1.3;
  }
`;

const MetaLine = styled(Caption2)`
  display: none;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
    color: rgb(${(props) => props.theme.colors.neutral400});
    margin-top: 4px;
    margin-left: 36px;
  }
`;

const DateCell = styled(Body)`
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-size: 1rem;
  padding: 0.5rem 0;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const Handle = styled.span`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: start;
  margin-left: -10px;
  cursor: grab;
  border-radius: 8px;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-size: 21px;
  touch-action: none;
  user-select: none;
  &:hover {
    background: rgb(${(props) => props.theme.colors.offWhite});
    color: rgb(${(props) => props.theme.colors.neutral600});
  }
  &:active {
    cursor: grabbing;
  }
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    order: -1;
    width: auto;
    height: auto;
    align-self: stretch;
    justify-self: stretch;
    margin-left: 0;
    border-radius: 0;
    font-size: 24px;
    background: rgb(${(props) => props.theme.colors.offWhite});
    border-right: 2px solid rgb(${(props) => props.theme.colors.neutral150});
    &:active {
      background: rgb(${(props) => props.theme.colors.neutral100});
    }
  }
`;

const QuickMenu = styled(motion.div)`
  display: flex;
  overflow: hidden;
  gap: 8px;
  flex-wrap: wrap;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 36px;
  }
`;

const QuickActionButton = styled(Button)`
  min-width: 0;
  width: fit-content;
  svg {
    min-height: 24px;
    min-width: 24px;
  }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.94);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PopoverContent = styled(Popover.Content)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 8px;
  background: rgb(${(props) => props.theme.colors.white});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 12px;
  box-shadow: ${(props) => props.theme.shadows.shadow600()};
  z-index: 10;
  transform-origin: var(--radix-popover-content-transform-origin);
  &[data-state="open"] {
    animation: ${popIn} 150ms ease-out;
  }
`;

const MenuButton = styled(Button)`
  display: inline-grid;
  min-width: 40px;
  width: 40px;
  height: 40px;
  padding: 0;
  align-self: center;
  font-size: 20px;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-right: 12px;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: fit-content;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: contents;
  }
`;

const DeleteCell = styled.div`
  display: flex;
  align-items: center;
  justify-self: end;
  @media screen and (max-width: ${NARROW_BREAKPOINT}px) {
    align-self: flex-start;
    margin-top: 0.5rem;
  }
`;

/**
 * @param {{
 *   memory: {memoryID: string, memoryTitle: string, date: {created: string, updated: string}},
 *   position: number,
 *   reorderMode: boolean,
 *   checked: boolean,
 *   onToggleSelect: (memoryID: string) => void,
 *   onDragEnd: () => void,
 *   quickMenuOpen: boolean,
 *   moved: boolean,
 *   onQuickMenuToggle: (memoryID: string | null) => void,
 *   onQuickMove: (memoryID: string, where: "top" | "bottom") => void,
 *   onScrollReorder: (memoryID: string, offset: number, direction: number) => void,
 *   isFirst: boolean,
 *   isLast: boolean,
 *   onDeleteRequest: (memoryID: string) => void,
 * }} props
 */
const ReorderableChapterRow = ({
  memory,
  position,
  reorderMode,
  checked,
  onToggleSelect,
  onDragEnd,
  quickMenuOpen,
  moved,
  onQuickMenuToggle,
  onQuickMove,
  onScrollReorder,
  isFirst,
  isLast,
  onDeleteRequest,
}) => {
  const controls = useDragControls();
  const [dragging, setDragging] = useState(false);

  const isNarrow = useMediaQuery({ maxWidth: NARROW_BREAKPOINT });

  // Framer Motion auto-scrolls the container while dragging near its edge,
  // but its reorder check is velocity-gated and the pointer is stationary
  // during auto-scroll, so no reorder fires. Track container scroll between
  // drag frames and step the order ourselves for scroll-driven movement;
  // pointer-driven reordering stays fully library-handled.
  const dragY = useMotionValue(0);
  const scrollStateRef = useRef(null);

  const handlePointerDown = (e) => {
    e.preventDefault();
    controls.start(e);
  };

  const quickActions = (
    <>
      <QuickActionButton
        disabled={isFirst}
        onButtonClick={() => onQuickMove(memory.memoryID, "top")}
      >
        <TbArrowBarUp /> Move to top
      </QuickActionButton>
      <QuickActionButton
        disabled={isLast}
        onButtonClick={() => onQuickMove(memory.memoryID, "bottom")}
      >
        <TbArrowBarDown /> Move to bottom
      </QuickActionButton>
    </>
  );

  return (
    <Row
      value={memory}
      style={{ y: dragY }}
      dragListener={false}
      dragControls={controls}
      $dragging={dragging}
      $moved={moved}
      data-memory-id={memory.memoryID}
      $reorderMode={reorderMode}
      onDragStart={(e) => {
        setDragging(true);
        onQuickMenuToggle(null);
        const container = getScrollParent(e.target);
        scrollStateRef.current = container
          ? { container, top: container.scrollTop }
          : null;
      }}
      onDrag={() => {
        const state = scrollStateRef.current;
        if (!state) return;
        const delta = state.container.scrollTop - state.top;
        state.top = state.container.scrollTop;
        if (delta !== 0) {
          onScrollReorder(memory.memoryID, dragY.get(), Math.sign(delta));
        }
      }}
      onDragEnd={() => {
        setDragging(false);
        scrollStateRef.current = null;
        onDragEnd();
      }}
    >
      {!reorderMode && (
        <CheckboxCell>
          <Checkbox
            checked={checked}
            selectItem={() => onToggleSelect(memory.memoryID)}
            style={CHECKBOX_STYLE}
          />
        </CheckboxCell>
      )}
      <PositionNumber>{position}</PositionNumber>
      {reorderMode ? (
        <>
          <TitleBlock $reorderMode>
            <TitleLine>
              <PositionBadge as="span">{position}</PositionBadge>
              <TitleCell as="span">{memory.memoryTitle}</TitleCell>
            </TitleLine>
            <MetaLine as="span">
              Created {formatDate(memory.date.created)}
              &nbsp;&nbsp;
              <span
                style={{
                  margin: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                •
              </span>
              &nbsp;&nbsp;Updated {formatDate(memory.date.updated)}
            </MetaLine>
            <AnimatePresence initial={false}>
              {!isNarrow && quickMenuOpen && (
                <QuickMenu
                  key="quick-menu"
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: "auto", opacity: 1, marginTop: 10 }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {quickActions}
                </QuickMenu>
              )}
            </AnimatePresence>
          </TitleBlock>
          <DateCell as="span">{formatDate(memory.date.created)}</DateCell>
          <ActionsCell>
            <Handle
              aria-label={`Move ${memory.memoryTitle}`}
              onPointerDown={handlePointerDown}
              title={`Move ${memory.memoryTitle}`}
            >
              <TbSeparatorHorizontal />
            </Handle>
            {isNarrow ? (
              <Popover.Root
                open={quickMenuOpen}
                onOpenChange={(open) =>
                  onQuickMenuToggle(open ? memory.memoryID : null)
                }
              >
                <Popover.Trigger asChild>
                  <MenuButton
                    icon
                    variant="tertiary"
                    disabled={!checked}
                    aria-label={`Reorder options for ${memory.memoryTitle}`}
                    title={
                      !checked
                        ? `"${memory.memoryTitle}" is not included in the book`
                        : `Reorder options for ${memory.memoryTitle}`
                    }
                  >
                    <TbDotsVertical />
                  </MenuButton>
                </Popover.Trigger>
                <Popover.Portal>
                  <PopoverContent
                    side="bottom"
                    align="end"
                    sideOffset={6}
                    collisionPadding={8}
                  >
                    {quickActions}
                  </PopoverContent>
                </Popover.Portal>
              </Popover.Root>
            ) : (
              <MenuButton
                icon
                variant="tertiary"
                disabled={!checked}
                aria-label={`Reorder options for ${memory.memoryTitle}`}
                title={
                  !checked
                    ? `"${memory.memoryTitle}" is not included in the book`
                    : `Reorder options for ${memory.memoryTitle}`
                }
                aria-expanded={quickMenuOpen}
                onButtonClick={() => onQuickMenuToggle(memory.memoryID)}
              >
                <TbDotsVertical />
              </MenuButton>
            )}
          </ActionsCell>
        </>
      ) : (
        <>
          <Link
            to={`edit/${memory.memoryID}`}
            state={{ memory }}
            style={{ display: "contents" }}
          >
            <TitleBlock>
              <TitleLine>
                <PositionPlain as="span">{position}</PositionPlain>
                <TitleCell as="span">{memory.memoryTitle}</TitleCell>
              </TitleLine>
              <ChapterDatePills date={memory.date} />
            </TitleBlock>
            <DateCell as="span">{formatDate(memory.date.created)}</DateCell>
            <DateCell as="span">{formatDate(memory.date.updated)}</DateCell>
          </Link>
          <DeleteCell>
            <MenuButton
              icon
              variant="warning"
              aria-label={`Delete "${memory.memoryTitle}"`}
              title={`Delete "${memory.memoryTitle}"`}
              onButtonClick={() => onDeleteRequest(memory.memoryID)}
            >
              <TbTrash />
            </MenuButton>
          </DeleteCell>
        </>
      )}
    </Row>
  );
};

export default ReorderableChapterRow;
